const CONDITION_GUIDE_MODAL = (() => {
  const getModalDialog = (topic) => {
    const dialogs = [...document.querySelectorAll('.condition-guide-modal__modal-dialog')];
    const matchingDialog = dialogs.find(dialog => dialog.dataset.topic === topic);
    if (matchingDialog) {
      return matchingDialog;
    }

    const dialogTemplate = document.querySelector(
      '.condition-guide-modal__modal-dialog-template',
    );
    const newDialog = dialogTemplate.content.cloneNode(true).querySelector(
      '.condition-guide-modal__modal-dialog',
    );
    newDialog.dataset.topic = topic;
    // De-duplicate modal close button id values. <modal-dialog> expects modal close buttons to have
    // id attributes, hence why we use id at all here.
    newDialog.querySelector('[id^="ModalClose-"]').id += `-${topic}`;

    // It doesn't really matter WHERE we append the new dialog as <modal-dialog> makes sure to
    // append itself to the document body no matter what.
    document.body.append(newDialog);

    return newDialog;
  };

  const getModalTitle = topic => getModalDialog(topic).querySelector(
    '.condition-guide-modal__title',
  );
  const getTabsContainer = topic => getModalDialog(topic).querySelector(
    '.condition-guide-modal__tabs-container',
  );
  const getTabContentsContainer = topic => getModalDialog(topic).querySelector(
    '.condition-guide-modal__tab-contents-container',
  );

  const getTabTitleTemplates = tabId => [
    ...document.querySelectorAll('.condition-guide-modal__tab-title-template'),
  ].filter(template => template.dataset.tabId === tabId);

  const getTabContentsTemplates = tabId => [
    ...document.querySelectorAll('.condition-guide-modal__tab-contents-template'),
  ].filter(template => template.dataset.tabId === tabId);

  const selectTab = (topic, tabId) => {
    [...getTabsContainer(topic).children].forEach((tab) => {
      tab.classList.toggle(
        'condition-guide-modal__tab-button--active',
        tab.dataset.tabId === tabId,
      );
    });
    const tabContentsContainer = getTabContentsContainer(topic);
    [...tabContentsContainer.children].forEach((tabContents) => {
      tabContents.classList.toggle('hidden', tabContents.dataset.tabId !== tabId);
    });
    [...tabContentsContainer.children]
      .find(tabContents => tabContents.dataset.tabId === tabId)
      ?.querySelectorAll('carousel-controls')
      .forEach((carouselControls) => {
        carouselControls.repositionControls();
      });
  };

  const selectFirstTab = (topic) => {
    selectTab(topic, getTabsContainer(topic).children[0]?.dataset.tabId);
  };

  const handleClickTab = (event) => {
    selectTab(
      event.currentTarget.closest('.condition-guide-modal__modal-dialog').dataset.topic,
      event.currentTarget.dataset.tabId,
    );
  };

  const constructTabButton = (tabId, innerContents) => {
    const buttonEl = document.createElement('button');
    buttonEl.type = 'button';
    buttonEl.className = 'condition-guide-modal__tab-button';
    buttonEl.dataset.tabId = tabId;
    buttonEl.addEventListener('click', handleClickTab);
    buttonEl.append(innerContents);
    return buttonEl;
  };

  const constructTabContents = (tabId, innerContents) => {
    const contentsEl = document.createElement('div');
    contentsEl.className = 'condition-guide-modal__tab-contents';
    contentsEl.dataset.tabId = tabId;
    contentsEl.append(innerContents);
    return contentsEl;
  };

  /**
   * Render or re-render the condition guide modal for the given topic. This doesn't open the modal,
   * it merely updates the modal's contents to match latest config.
   * 
   * NOTE: The ability to re-render the modal is useful inside the theme editor, where we want to be
   * able re-render the modal when a site manager adds or removes a Condition Guide Modal Tab
   * section. Outside of the theme editor, we expect rendering of the condition guide modal to be a
   * one-time thing.
   */
  const renderModal = (topic) => {
    const { title: modalTitleText, tabIds } = CONDITION_GUIDE_MODAL_CONFIG.topics[topic];

    getModalTitle(topic).textContent = modalTitleText;

    const tabsContainer = getTabsContainer(topic);
    const tabContentsContainer = getTabContentsContainer(topic);

    // Remember the selected tab when dynamically updating modal contents in the Shopify theme
    // editor.
    const selectedTabContents = [...tabContentsContainer.children].find(
      tabContents => !tabContents.matches('.hidden'),
    );
    const selectedTabId = selectedTabContents?.dataset.tabId;
    const currentScrollTop = selectedTabContents?.scrollTop;

    tabsContainer.replaceChildren();
    tabContentsContainer.replaceChildren();

    tabIds.forEach((tabId) => {
      const titleTemplates = getTabTitleTemplates(tabId);
      const title = titleTemplates.length === 1 ?
        titleTemplates[0].content.cloneNode(true) :
        'Error';
      tabsContainer.append(constructTabButton(tabId, title));
      const contentsTemplates = getTabContentsTemplates(tabId);
      let contents;
      if (contentsTemplates.length === 1) {
        contents = contentsTemplates[0].content.cloneNode(true);
      } else if (contentsTemplates.length > 1) {
        contents = `Error: Duplicate condition-guide-modal-tab sections for tab ID '${tabId}'.`;
      } else {
        contents = `Error: Missing condition-guide-modal-tab section for tab ID '${tabId}'.`;
      }
      tabContentsContainer.append(constructTabContents(tabId, contents));
    });

    // Hide the tab buttons when there's only one tab.
    tabsContainer.classList.toggle('hidden', tabsContainer.children.length <= 1);

    if (selectedTabId !== undefined) {
      const newTabContents = [...tabContentsContainer.children].find(
        tabContents => tabContents.dataset.tabId === selectedTabId,
      );
      if (newTabContents) {
        newTabContents.scrollTop = currentScrollTop;
        selectTab(topic, selectedTabId);
      } else {
        selectFirstTab(topic);
      }
    }
  };

  const renderModals = () => {
    Object.keys(CONDITION_GUIDE_MODAL_CONFIG.topics).forEach((topic) => {
      renderModal(topic);
    });
  };

  const openModal = (event) => {
    const topic = event
      .currentTarget
      .closest('.condition-guide-modal__opener-container')
      .dataset
      .topic;
    const tabContentsContainer = getTabContentsContainer(topic);
    // Scroll all tabs to the top
    [...tabContentsContainer.children].forEach((tabContents) => {
      // Remove hidden, otherwise assigning to scrollTop has no effect. The selectFirstTab call
      // below will re-apply hidden as appropriate.
      tabContents.classList.remove('hidden');
      tabContents.scrollTop = 0;
    });
    selectFirstTab(topic);
    getTabsContainer(topic).scrollTo({ left: 0 });
    // Reset any image carousels
    tabContentsContainer.querySelectorAll('carousel-controls').forEach((carouselControls) => {
      carouselControls.itemIndex = 0;
    });
    getModalDialog(topic).setAttribute('open', 'open');
    document.body.classList.add('overflow-hidden');
  };

  document.addEventListener('DOMContentLoaded', () => {
    renderModals();
    [
      ...document.querySelectorAll('.condition-guide-modal__opener'),
    ].forEach((opener) => {
      opener.addEventListener('click', openModal);
    });
  });

  return {
    // "Exporting" of renderModals is for the benefit of condition-guide-modal-design-mode.js.
    renderModals,
  };
})();
